import { AfterViewInit, Component, forwardRef, Inject, Input, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { PDF_COVER_TEMPLATE, PDF_FOOTER_TEMPLATE, PDF_HEADER_TEMPLATE } from '../../common/endpoints';
import { TemplateLoaderService } from '../../dashboard-area/template-loader/template-loader.service';
import { AbstractExportContextService, ReportPdfTemplates } from '../../service/abstract-export-context.service';
import { DownloadService, ExportPdfMode } from '../../service/download.service';
import { UiService } from '../../service/ui.service';
import { AbstractContextService } from '../../shared/class/abstract-context-service.class';
import { AbstractThingContextService } from '../../shared/class/abstract-thing-context-service.class';
import { SnackbarService } from '../shared/snackbar.service';
import { WidgetPrintResizeCoordinatorService } from '../shared/widget-print-resize-coordinator.service';

@Component({
    selector: 'report-pdf',
    template: '',
    styles: [`
        ::ng-deep .pdf-cover {
            display: none;
        }

        ::ng-deep .export-pdf .pdf-cover {
            display: block;
        }

        ::ng-deep .export-pdf .pdf-cover-page-break {
            break-before: page;
            page-break-before: always;
        }

        ::ng-deep .report-mode-pdf-cover-page-break {
            break-before: page;
            page-break-before: always;
        }
    `]
})
export class ReportPdfComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() reportTitle: string = "";

    @Input() mode: ExportPdfMode = ExportPdfMode.REMOTE;

    @Input() fileName: string;

    @Input() coverTemplateName: string;

    @Input() headerTemplateName: string;

    @Input() footerTemplateName: string;

    @Input() periodRef: string;

    constructor(
        @Inject(forwardRef(() => TemplateLoaderService)) private loader: TemplateLoaderService,
        @Inject(forwardRef(() => DownloadService)) private downloadService: DownloadService,
        @Inject(forwardRef(() => ViewContainerRef)) private vcRef: ViewContainerRef,
        @Inject(forwardRef(() => UiService)) private uiService: UiService,
        @Inject(forwardRef(() => AbstractContextService)) private contextService: AbstractContextService,
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => AbstractExportContextService)) private exportService: AbstractExportContextService,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) private widgetPrintResizeCoordinator: WidgetPrintResizeCoordinatorService,
        @Inject(forwardRef(() => SnackbarService)) private snackbarService: SnackbarService
    ) { }

    private _releasePrintResizeMode: (() => void) | null = null;
    private preparedTemplates: Promise<ReportPdfTemplates> | null = null;
    private unresolvedPlaceholders: string[] = [];

    preTemplate: string = `<div [ngClass]="reportMode ? 'report-mode-pdf-cover' : 'pdf-cover'">`;
    postTemplate: string = `</div><div [ngClass]="reportMode ? 'report-mode-pdf-cover-page-break' : 'pdf-cover-page-break'"></div>`; //page break

    ngOnInit(): void {
        this.downloadService.setExportPdfButtonVisible();
        this.downloadService.setExportPdfMode(this.mode);
        this.exportService.setReportPdfFileName(this.fileName || (this.reportTitle.toLocaleLowerCase()));
        this.exportService.setPdfPeriodRef(this.periodRef);
        this.exportService.setReportPdfTemplatesProvider(() => this.prepareReportPdfTemplates());
        this.loadCover();
    }

    // the cover keeps its own DOM rendering; it only shares the lookup rules with the prepared templates
    private loadCover(): void {
        const coverOverride = this.getCoverOverride();
        if (coverOverride === '') return; // the report generation supplied an empty cover: no cover and no page break
        if (coverOverride === null && !this.hasCover()) return;

        const templateWrapper = this.getCoverTemplateWrapper();
        if (coverOverride !== null) {
            this.loader.loadFromTextWithWrapper(coverOverride, templateWrapper, this.vcRef, true);
            return;
        }
        if (!this.coverTemplateName?.trim()) {
            this.loader.loadFromEndpoint(PDF_COVER_TEMPLATE, this.vcRef, true, null, templateWrapper);
            return;
        }
        const coverTemplateName = this.coverTemplateName.trim();
        const thingDefinitionId = this.getThingDefinitionId();
        this.uiService.getTemplateContentSelection(coverTemplateName, thingDefinitionId, true)
            .then(selection => {
                if (selection.found) {
                    // found but empty: no cover, no page break
                    if (selection.content === '') return Promise.resolve();
                    return this.loader.loadFromTextWithWrapper(this.escapeCoverPlaceholders(selection.content), templateWrapper, this.vcRef, true);
                }
                return this.loader.loadFromEndpoint(PDF_COVER_TEMPLATE, this.vcRef, true, null, templateWrapper);
            })
            .catch(() => this.loader.loadFromEndpoint(PDF_COVER_TEMPLATE, this.vcRef, true, null, templateWrapper));
    }

    private getCoverTemplateWrapper(): { preTemplate: string, postTemplate: string, placeholders: { [key: string]: string } } {
        const periodValue = this.exportService.getPdfPeriodRefValue();
        const placeholders = { "${reportTitle}": this.exportService.resolveExportFileNamePlaceholders(this.reportTitle, periodValue?.start, periodValue?.end) || "" };
        return { preTemplate: this.preTemplate, postTemplate: this.postTemplate, placeholders: placeholders };
    }

    /**
     * Returns the already selected and resolved cover supplied by the report generation (injected by the report renderer before the
     * page starts), or {@code null} if none was supplied. An empty string is a supplied value meaning "no cover".
     */
    private getCoverOverride(): string | null {
        const cover = (window as any).__servitlyReportGeneration?.cover;
        return typeof cover === 'string' ? cover : null;
    }

    private hasCover(): boolean {
        return !!this.coverTemplateName || !!this.reportTitle;
    }

    // lazy: only a user-driven export needs the values
    private prepareReportPdfTemplates(): Promise<ReportPdfTemplates> {
        if (!this.preparedTemplates) {
            this.unresolvedPlaceholders = [];
            this.preparedTemplates = Promise.all([
                this.prepareCoverTemplate(),
                this.prepareNamedTemplate(this.headerTemplateName, PDF_HEADER_TEMPLATE),
                this.prepareNamedTemplate(this.footerTemplateName, PDF_FOOTER_TEMPLATE)
            ]).then(([cover, header, footer]) => {
                this.warnUnresolvedPlaceholders(this.unresolvedPlaceholders, true);
                return { cover: cover, header: header, footer: footer };
            });
        }
        return this.preparedTemplates;
    }

    private prepareCoverTemplate(): Promise<string | null> {
        const coverOverride = this.getCoverOverride();
        if (coverOverride !== null) return Promise.resolve(coverOverride);
        if (!this.hasCover()) return Promise.resolve(null);
        return this.resolveTemplate(this.coverTemplateName, PDF_COVER_TEMPLATE, true);
    }

    private prepareNamedTemplate(templateName: string, legacyTemplateEndpoint: string): Promise<string | null> {
        return this.resolveTemplate(templateName, legacyTemplateEndpoint);
    }

    // hierarchy, then General; absent or missing uses the legacy template; found but empty stays empty
    private resolveTemplate(templateName: string, legacyTemplateEndpoint: string, escapeForAngular: boolean = false): Promise<string | null> {
        const legacyTemplate = () => this.loader.getTextFromEndpoint(legacyTemplateEndpoint);
        const selection = templateName?.trim()
            ? this.uiService.getTemplateContentSelection(templateName.trim(), this.getThingDefinitionId(), true)
                .then(result => result.found ? result.content : legacyTemplate())
            : legacyTemplate();
        return selection
            .catch(() => legacyTemplate())
            .then(content => content == null ? null : this.resolveTemplatePlaceholders(content, escapeForAngular))
            .catch(() => null);
    }

    private resolveTemplatePlaceholders(content: string, escapeForAngular: boolean): string {
        if (!content) return content;
        const roots = this.getPlaceholderRoots();
        return content.replace(/\$\{([^{}]+)\}/g, (placeholder: string, expression: string) => {
            const value = this.findPlaceholderValue(expression.trim(), roots);
            if (value != null) return value;
            if (this.unresolvedPlaceholders.indexOf(placeholder) < 0) this.unresolvedPlaceholders.push(placeholder);
            return escapeForAngular ? ReportPdfComponent.escapeBraces(placeholder) : placeholder;
        });
    }

    // Angular compiles the cover: a bare "{" would break it, so unresolved placeholders are written as entities
    private static escapeBraces(placeholder: string): string {
        return placeholder.replace(/\{/g, '&#123;').replace(/\}/g, '&#125;');
    }

    // the DOM path only warns about placeholders the export could not resolve either
    private escapeCoverPlaceholders(content: string): string {
        const roots = this.getPlaceholderRoots();
        const unresolved: string[] = [];
        const escaped = content.replace(/\$\{([^{}]+)\}/g, (placeholder: string, expression: string) => {
            if (expression.trim() === 'reportTitle') return placeholder;
            if (this.findPlaceholderValue(expression.trim(), roots) == null && unresolved.indexOf(placeholder) < 0) unresolved.push(placeholder);
            return ReportPdfComponent.escapeBraces(placeholder);
        });
        this.warnUnresolvedPlaceholders(unresolved, false);
        return escaped;
    }

    // the DOM path also runs in the printed page: console only, a snackbar would end up in the PDF
    private warnUnresolvedPlaceholders(placeholders: string[], showSnackbar: boolean): void {
        if (!placeholders.length) return;
        const messages = placeholders.map(placeholder => 'Placeholder "' + placeholder + '" is not supported yet in on-demand generation and was left unresolved.');
        messages.forEach(message => console.warn(message));
        if (showSnackbar) {
            this.snackbarService.showSnackbar(messages.join('\n'), 'notification-info', false);
        }
    }

    // on a Thing page the Thing's own customer, location and partner win over the page context
    private getPlaceholderRoots(): { [root: string]: any } {
        const thing = this.thingContextService.getCurrentThing();
        const roots: { [root: string]: any } = {};
        const thingDefinition = thing?.thingDefinition || this.thingContextService.getCurrentThingDefinition();
        const location = thing?.location || this.contextService.getCurrentLocation();
        const customer = thing?.customer || this.contextService.getCurrentCustomer();
        const partner: any = thing?.location?.partner || this.contextService.getCurrentPartner();
        const organization = this.contextService.getCurrentOrganization() || partner?.organization;
        if (thing) roots.thing = thing;
        if (thingDefinition) roots.thingDefinition = thingDefinition;
        if (location) roots.location = location;
        if (customer) roots.customer = customer;
        if (partner) roots.partner = partner;
        if (organization) roots.organization = organization;
        roots.reportTitle = this.getResolvedReportTitle();
        return roots;
    }

    private findPlaceholderValue(expression: string, roots: { [root: string]: any }): string | null {
        const parts = expression.split('.');
        let value: any = roots[parts[0]];
        for (let i = 1; i < parts.length && value != null; i++) {
            value = value[parts[i]];
        }
        if (value == null || typeof value === 'object') {
            return null;
        }
        return String(value);
    }

    private getResolvedReportTitle(): string {
        const periodValue = this.exportService.getPdfPeriodRefValue();
        return this.exportService.resolveExportFileNamePlaceholders(this.reportTitle, periodValue?.start, periodValue?.end) || "";
    }

    private getThingDefinitionId(): string | null {
        const thing = this.thingContextService.getCurrentThing();
        return thing ? thing.thingDefinitionId : null;
    }

    ngAfterViewInit(): void {
        this._releasePrintResizeMode = this.widgetPrintResizeCoordinator.activate();
    }

    ngOnDestroy(): void {
        this._releasePrintResizeMode?.();
        this._releasePrintResizeMode = null;
        this.downloadService.setExportPdfButtonInvisible();
    }
}
